import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Button } from '../ui/button';
import { ArrowRight, Receipt, ArrowDown } from 'lucide-react';

const SMOOTH = [0.22, 1, 0.36, 1];

const PROPERTY_LINKS = [
  { label: 'BIOACTIVE', href: 'https://flexioss.pl/wp-content/uploads/2021/03/The-comparison-study-of-bioactivity-between-composites-containing.pdf' },
  { label: 'OSTEOCONDUCTIVE', href: 'https://flexioss.pl/wp-content/uploads/2025/05/Preliminary-Results-of-Surgical-Treatment-for-Enchondroma.pdf' },
  { label: 'FLEXIBLE', href: 'https://flexioss.pl/wp-content/uploads/2021/03/Application-of-B-13-glucan-in-production-of.pdf' },
  { label: 'RESORBABLE', href: 'https://flexioss.pl/wp-content/uploads/2017/10/Journal_of_Biomedical_Materials_Research_Part_B__Applied_Biomaterials_2018.pdf' },
];

const HeroSection = ({ onContactClick }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });

  const wordmarkY = useTransform(scrollYProgress, [0, 1], [0, 150]);
  const wordmarkScale = useTransform(scrollYProgress, [0, 1], [1, 1.14]);
  const wordmarkOpacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);
  const brieferY = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const brieferOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const objectY = useTransform(scrollYProgress, [0, 1], [0, -70]);
  const objectScale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);

  return (
    <section ref={ref} id="hero" data-testid="hero-section" className="relative h-screen overflow-hidden bg-[#000a14]">
      <div className="sticky top-0 flex h-screen flex-col items-center justify-center overflow-hidden">
        {/* Layered cinematic background */}
        <motion.div style={{ y: bgY }} className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_50%_30%,rgba(11,38,68,0.55),transparent_70%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_40%_40%_at_80%_80%,rgba(232,122,32,0.10),transparent_70%)]" />
        </motion.div>
        {/* Floating particles */}
        <div className="absolute inset-0">
          {[...Array(26)].map((_, i) => (
            <span
              key={i}
              className="absolute h-px w-px rounded-full bg-white/60"
              style={{
                top: `${(i * 53) % 100}%`,
                left: `${(i * 37) % 100}%`,
                opacity: 0.15 + (i % 5) * 0.12,
                animation: `heroFloat ${12 + (i % 8) * 2}s ease-in-out ${i % 6}s infinite`,
              }}
            />
          ))}
        </div>

        <div className="relative z-10 flex w-full flex-col items-center px-4">
          {/* Brand eyebrow */}
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: SMOOTH }}
            className="mb-3 font-display text-[10px] uppercase tracking-[0.42em] text-white/45 sm:text-xs"
          >
            Fortschritt Medical Supplies · Philippines
          </motion.p>

          {/* Floating product object — the hero centrepiece, above the wordmark */}
          <motion.div
            style={{ y: objectY, scale: objectScale, opacity: wordmarkOpacity }}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, ease: SMOOTH }}
            className="relative z-20 mb-1 flex justify-center sm:mb-2"
          >
            <img
              src="/flexioss-transparent.png"
              alt=""
              aria-hidden="true"
              className="hero-product-float w-[80vw] max-w-[420px] object-contain drop-shadow-[0_55px_120px_rgba(59,130,246,0.6)] sm:w-[64vw] sm:max-w-[600px] lg:w-[47vw] lg:max-w-[820px]"
            />
          </motion.div>

          {/* Readable wordmark — FLEXIOSS */}
          <motion.h1
            style={{ y: wordmarkY, scale: wordmarkScale, opacity: wordmarkOpacity }}
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: SMOOTH }}
            className="relative z-10 flex w-full select-none items-baseline justify-center font-display font-bold not-italic uppercase leading-none tracking-[-0.01em] text-white"
          >
            <span className="sr-only">FlexiOss Bone Regenerative Biomaterial</span>
            <span aria-hidden="true" className="text-[13vw] sm:text-[10vw] lg:text-[6vw]">Flexi</span>
            <span aria-hidden="true" className="text-[13vw] sm:text-[10vw] lg:text-[6vw] text-white/55">Oss</span>
          </motion.h1>

          {/* Subtitle rule */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="mt-3 flex w-full max-w-3xl items-center gap-4"
          >
            <span className="h-px flex-1 bg-gradient-to-r from-transparent to-white/25" />
            <span className="whitespace-nowrap font-display text-[10px] uppercase tracking-[0.32em] text-white/55 sm:text-xs">
              Third-Generation Tricurean Bone Filler
            </span>
            <span className="h-px flex-1 bg-gradient-to-l from-transparent to-white/25" />
          </motion.div>

          {/* (cost briefer relocated to the Total Cost-of-Care section) */}

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.7, ease: SMOOTH }}
            className="mt-7 flex flex-col items-center gap-3 sm:flex-row"
          >
            <Button
              size="lg"
              onClick={() => onContactClick('samples')}
              data-testid="hero-cta-btn"
              className="rounded-none bg-blue-600 px-8 py-6 text-sm font-bold uppercase tracking-[0.12em] text-white transition-all duration-500 hover:bg-blue-500 hover:shadow-[0_0_40px_rgba(37,99,235,0.45)]"
            >
              Request for Samples <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.6} />
            </Button>
            <Button
              size="lg"
              onClick={() => onContactClick('pricing')}
              data-testid="hero-pricing-btn"
              className="rounded-none border border-[#E87A20] bg-transparent px-8 py-6 text-sm font-bold uppercase tracking-[0.12em] text-[#E87A20] transition-all duration-500 hover:bg-[#E87A20] hover:text-white"
            >
              Request Pricing <Receipt className="ml-2 h-4 w-4" strokeWidth={1.6} />
            </Button>
          </motion.div>

          {/* Evidence traits — premium editorial lockup (no button chrome) */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-8 flex flex-wrap items-center justify-center gap-x-5 gap-y-2.5 sm:gap-x-7"
          >
            {PROPERTY_LINKS.map((p, i) => (
              <React.Fragment key={p.label}>
                {i > 0 && <span aria-hidden="true" className="hidden h-3 w-px bg-white/15 sm:block" />}
                <a
                  href={p.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`badge-${p.label.toLowerCase()}`}
                  className="group relative text-[10px] font-semibold uppercase tracking-[0.32em] text-blue-100/80 transition-colors duration-300 hover:text-white sm:text-[11px]"
                  style={{ textShadow: '0 0 18px rgba(96,165,250,0.45)' }}
                >
                  {p.label}
                  <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-gradient-to-r from-blue-400 to-[#E87A20] transition-all duration-300 group-hover:w-full" />
                </a>
              </React.Fragment>
            ))}
          </motion.div>

          {/* Skeptic → FAQ — premium inline editorial link */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="mt-6 flex justify-center"
          >
            <Link
              to="/faq"
              data-testid="hero-hydroxyapatite-faq"
              className="group inline-flex items-center gap-2.5 text-xs transition-colors duration-300"
            >
              <span className="font-serif italic text-slate-400 transition-colors duration-300 group-hover:text-slate-200">
                Skeptical about hydroxyapatite?
              </span>
              <span className="inline-flex items-center gap-1.5 font-semibold uppercase tracking-[0.18em] text-blue-300 transition-colors duration-300 group-hover:text-white">
                See the data
                <ArrowRight className="h-3 w-3 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2} />
              </span>
            </Link>
          </motion.div>
        </div>

        {/* Scroll cue — only on wide + tall-enough viewports so it never collides with the FAQ link */}
        <motion.div
          style={{ opacity: brieferOpacity }}
          className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 text-white/30 [@media(min-width:640px)_and_(min-height:861px)]:block"
        >
          <ArrowDown className="h-5 w-5 animate-bounce" strokeWidth={1.4} />
        </motion.div>
      </div>

      <style>{`
        @keyframes heroFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-14px); }
        }
        @keyframes heroProductFloat {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-12px) rotate(-1deg); }
        }
        .hero-product-float { animation: heroProductFloat 6s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) { .hero-product-float { animation: none; } }
      `}</style>
    </section>
  );
};

export default HeroSection;
